// src/app/core/services/progress-tracker.service.ts
import { Injectable, inject } from '@angular/core';
import { Firestore, doc, updateDoc, setDoc, getDoc, Timestamp, serverTimestamp } from '@angular/fire/firestore';
import { Auth } from '@angular/fire/auth';
import { UserModel, UserJourneyStatus, LastStep } from '@core/models/user.model';
import { ProgressStep } from '@core/models/progress-steps.types';
import { PROGRESS_STEPS } from '@core/constants/progress-steps.constants';
import { StepPayload } from '@core/models/progress-payload-map';
import { ProgressStepValue } from '@core/constants/progress-steps.constants';

@Injectable({
  providedIn: 'root'
})
export class ProgressTrackerService {
  private firestore = inject(Firestore);
  private auth = inject(Auth);

  /* =====================================================
   * AJUSTE REALIZADO (ITEM 5.2)
   * -----------------------------------------------------
   * Nenhum ajuste funcional foi necessário.
   *
   * O ProgressTrackerService já estava:
   * - alinhado ao UserModel atualizado (item 5.1)
   * - utilizando subscriptionCycle como fonte da verdade
   * - lendo evaluationId corretamente
   * - semanticamente consistente com a jornada do usuário
   *
   * Código mantido integralmente para preservar estabilidade
   * e evitar alterações desnecessárias.
   * ===================================================== */
  private uid: string | null = null;

  // === CACHE LOCAL ===
  private lastStepCache: LastStep = null;
  private lastStepTimestamp: number = 0;
  private readonly CACHE_TTL = 30_000; // 30 segundos

  constructor() {
    this.initAuthListener();
  }

  private initAuthListener(): void {
    this.auth.onAuthStateChanged(user => {
      this.uid = user?.uid ?? null;
      // Invalida cache ao trocar de usuário
      this.invalidateCache();
    });
  }

  private get userRef() {
    if (!this.uid) throw new Error('Usuário não autenticado.');
    return doc(this.firestore, `users/${this.uid}`);
  }

  // === INVALIDAÇÃO DO CACHE ===
  private invalidateCache(): void {
    this.lastStepCache = null;
    this.lastStepTimestamp = 0;
  }

  /**
   * Aguarda a resolução do Firebase Auth após reload (F5).
   * Evita condição de corrida onde getCurrentProgress é chamado
   * antes do onAuthStateChanged popular o uid.
   */
  private async waitForAuth(): Promise<void> {
    if (this.uid) return;
    await new Promise<void>((resolve, reject) => {
      const unsubscribe = this.auth.onAuthStateChanged(user => {
        unsubscribe();
        if (!user) {
          reject(new Error('Usuário não autenticado'));
          return;
        }
        this.uid = user.uid;
        resolve();
      });
    });
  }

  async saveProgressTyped<T extends ProgressStepValue>(
    step: T,
    status: UserJourneyStatus,
    payload: StepPayload<T>
  ): Promise<void> {
    // Reaproveita o método atual
    return this.saveGenericProgress(step, status, payload);
  }

  // === SALVAR PROGRESSO (invalida cache após salvar) ===
  public async saveProgress(
    step: ProgressStep | null,
    status?: UserJourneyStatus,
    extra?: {
      evaluationProgress?: Partial<UserModel['evaluationProgress']>;
      trainingProgress?: Partial<UserModel['trainingProgress']>;
    } & Record<string, unknown>
   ): Promise<void> {
    const updateData: Record<string, unknown> = {
      lastStep: step,
      updatedAt: serverTimestamp() as Timestamp
    };

    if (status !== undefined) {
      updateData['userJourneyStatus'] = status;
    }

    if (extra?.evaluationProgress) {
      const evalProgress = extra.evaluationProgress;

      if (evalProgress.questionnaireStep !== undefined) {
        updateData['evaluationProgress.questionnaireStep'] = evalProgress.questionnaireStep;
      }

      if (evalProgress.photoStep !== undefined) {
        updateData['evaluationProgress.photoStep'] = evalProgress.photoStep;
      }

      if (evalProgress.additionalQuestionStep !== undefined) {
        updateData['evaluationProgress.additionalQuestionStep'] = evalProgress.additionalQuestionStep;
      }
    }

    if (extra?.trainingProgress) {
      const train = extra.trainingProgress;

      if (train.currentWorkout !== undefined) {
        updateData['trainingProgress.currentWorkout'] = train.currentWorkout;
      }

      if (train.currentExercise !== undefined) {
        updateData['trainingProgress.currentExercise'] = train.currentExercise;
      }
    }

    // ======================================================
    // CAMPOS GENÉRICOS (CRÍTICO PARA SUBSCRIPTION)
    // ======================================================
    if (extra) {
      for (const [key, value] of Object.entries(extra)) {
        if (
          key !== 'evaluationProgress' &&
          key !== 'trainingProgress' &&
          value !== undefined
        ) {
          updateData[key] = value;
        }
      }
    }

    try {
      await updateDoc(this.userRef, updateData);
    } catch (error) {
      if ((error as any)?.code === 'not-found') {
        await setDoc(this.userRef, updateData, { merge: true });
      } else {
        throw error;
      }
    }
    // Invalida cache após salvar
    this.invalidateCache();
  }

  // Método unificado que injeta updatedAt automaticamente.
  private async updateUser(data: Partial<UserModel>) {
    const payload = {
      ...data,
      updatedAt: serverTimestamp() as Timestamp
    };
    try {
      await updateDoc(this.userRef, payload);
    } catch (error) {
      if ((error as any)?.code === 'not-found') {
        await setDoc(this.userRef, payload, { merge: true });
      } else {
        throw error;
      }
    }
    this.invalidateCache();
  }

  /* =====================================================
  * STATUS DA AVALIAÇÃO (subscriptionCycle.evaluation.status)
  * ===================================================== */
  private async updateEvaluationStatus(
    status: 'available' | 'in_progress' | 'completed'
  ): Promise<void> {

    const update: any = {
      'subscriptionCycle.evaluation.status': status
    };

    if (status === 'in_progress') {
      update['subscriptionCycle.evaluation.startedAt'] = serverTimestamp();
    }

    if (status === 'completed') {
      update['subscriptionCycle.evaluation.completedAt'] = serverTimestamp();
    }

    try {
      await updateDoc(this.userRef, update);
    } catch (error) {
      if ((error as any)?.code === 'not-found') {
        await setDoc(this.userRef, update, { merge: true });
      } else {
        throw error;
      }
    }

    this.invalidateCache();
  }

  /* =====================================================
   * MÉTODOS DE SALVAR PROGRESSO
   * ===================================================== */
  // Método público para salvar o progresso
  async saveGenericProgress(
    step: LastStep,
    status: UserJourneyStatus,
    extra?: Record<string, unknown>
  ): Promise<void> {
    await this.saveProgress(step, status, extra);
  }

  async saveTermsAccepted(): Promise<void> {
    await this.saveProgress(PROGRESS_STEPS.TERMS_ACCEPTED, 'user_data_pending');
  }

  async saveUserDataCompleted(): Promise<void> {
    await this.saveProgress(PROGRESS_STEPS.USER_DATA_COMPLETED, 'email_confirmation_pending');
  }

  // === CONFIRMAÇÃO DE E-MAIL CONCLUÍDA ===
  async completeEmailConfirmation(): Promise<void> {
    // IMPORTANTE:
    // - emailVerified / emailVerifiedAt são server-only (Cloud Function / Admin SDK)
    // - O client só avança fluxo (lastStep/userJourneyStatus)
    await this.updateUser({
      lastStep: PROGRESS_STEPS.EMAIL_CONFIRMED,
      userJourneyStatus: 'subscription_available',
    });
  }

  // === ASSINATURA: Agora é simples e limpo (sempre começa do zero) ===
  async startSubscriptionFlow(): Promise<void> {
    // =====================================================
    // VERIFICAÇÃO ADICIONADA: Evita sobrescrever estado existente
    // -----------------------------------------------------
    // Se o lastStep já indicar que o fluxo de assinatura está em progresso,
    // não sobrescreve para evitar reset acidental ou loop.
    // Isso permite iniciar o fluxo normalmente quando o usuário confirma o e-mail
    // (lastStep ainda é "email_confirmed"), mas ignora chamadas repetidas após o início.
    // =====================================================
    const current = await this.getCurrentProgress();

    const blockedSteps: LastStep[] = [
      PROGRESS_STEPS.SUBSCRIPTION_STARTED,
      PROGRESS_STEPS.SUBSCRIPTION_CHECKOUT_CREATED,
      PROGRESS_STEPS.SUBSCRIPTION_CHECKOUT_REDIRECTED,
      PROGRESS_STEPS.SUBSCRIPTION_PAYMENT_PROCESSING,
      PROGRESS_STEPS.SUBSCRIPTION_PAYMENT_FAILED,
    ];

    if (blockedSteps.includes(current.lastStep)) {
      console.log(
        'startSubscriptionFlow: fluxo já em andamento → ignorando',
        current.lastStep
      );
      return;
    }

    if (blockedSteps.includes(current.lastStep)) {
      console.log(
        'startSubscriptionFlow: fluxo já em andamento → ignorando',
        current.lastStep
      );
      return;
    }

    if (blockedSteps.includes(current.lastStep as any)) {
      console.log('startSubscriptionFlow: Fluxo já em andamento → ignorando');
      return;
    }

    // Apenas marca que o usuário entrou no fluxo de assinatura
    await this.saveProgress(
      PROGRESS_STEPS.SUBSCRIPTION_STARTED,
      'subscription_pending'
    );
  }

  async startQuestionnaire(): Promise<void> {
    // Caminho A: client-only (compatível com rules novas)
    await this.saveProgress(
      PROGRESS_STEPS.QUESTIONNAIRE_STARTED,
      'evaluation_in_progress'
    );

    // Atualiza estado da entidade avaliação
    await this.updateEvaluationStatus('in_progress');

    return;
  }

  async saveQuestionnaireCompleted(): Promise<void> {
    await this.saveProgress(
      PROGRESS_STEPS.QUESTIONNAIRE_COMPLETED,
      'evaluation_in_progress',
      {
        evaluationProgress: {
          questionnaireStep: 999
        }
      }
    );
  }

  async savePhotoStep(
    photoIndex: number,
    questionAnswered = false
  ): Promise<void> {

    const step: LastStep = questionAnswered
      ? `additional_question_${photoIndex}`
      : `photo_analysis_${photoIndex}`;

    await this.saveProgress(step, 'evaluation_in_progress', {
      evaluationProgress: {
        photoStep: photoIndex,
        additionalQuestionStep: questionAnswered ? photoIndex : undefined
      }
    });
  }

  async saveWorkoutStep(
    workout: number,
    exercise: number
  ): Promise<void> {
    const step: ProgressStep = `workout_${workout}_exercise_${exercise}`;
    await this.saveProgress(step, 'training_active', {
      trainingProgress: {
        currentWorkout: workout,
        currentExercise: exercise
      }
    });
  }

  async completeEvaluation(): Promise<void> {
    const current = await this.getCurrentProgress();

    // Caminho A: NÃO atualiza subscriptionCycle no client (server-only nas rules)

    // Mantém o comportamento atual (jornada + cursor final)
    await this.saveProgress(
      PROGRESS_STEPS.EVALUATION_COMPLETED,
      'evaluation_completed',
      {
        evaluationProgress: {
          ...current.evaluation,
          photoStep: 10,
          additionalQuestionStep: 10
        }
      }
    );

    // Atualiza estado da avaliação
    await this.updateEvaluationStatus('completed');
  }

  async saveSubscriptionStep(step: ProgressStep) {
    await this.saveProgress(step, 'subscription_pending');
  }

  /* =====================================================
   * LEITURA DO PROGRESSO (com cache)
   * ===================================================== */
  async getCurrentProgress(): Promise<{
    userJourneyStatus: UserJourneyStatus;
    lastStep: LastStep;
    evaluation: UserModel['evaluationProgress'];
    training: UserModel['trainingProgress'];
    subscriptionStatus?: UserModel['subscriptionStatus'];
    trainingProgram?: UserModel['trainingProgram'];
    evaluationId?: string | null;
    subscriptionCycle?: UserModel['subscriptionCycle'];
    hasSubscriptionHistory?: UserModel['hasSubscriptionHistory'];
    hasSeenWelcomeSubscription?: boolean;
    planId?: string;
    commercialPriceCents?: number;
    commitmentDurationMonths?: number;
    billingInterval?: 'month';
    billingIntervalCount?: number;
    installmentCount?: number;
    installmentAmountCents?: number;
    billingPriceCents?: number;
    currency?: string;
  }> {
    // Aguarda autenticação em casos de reload (F5)
    await this.waitForAuth();

    // Leitura única com getDoc (sem docData reativo)
    const snap = await getDoc(this.userRef);
    if (!snap.exists()) {
      throw new Error('Documento do usuário não encontrado');
    }
    const data = snap.data() as Partial<UserModel>;

    const defaultEval = {
      questionnaireStep: 0,
      photoStep: 0,
      additionalQuestionStep: 0
    };

    const defaultTrain = {
      currentWorkout: 1,
      currentExercise: 1
    };

    const trainingProgram = data.trainingProgram ?? null;

    // ===============================
    // NORMALIZAÇÃO ROBUSTA (CORE)
    // ===============================
    let evaluation = {
      ...defaultEval,
      ...data.evaluationProgress
    };
    const lastStep = data.lastStep ?? null;

    // === Avaliação por fotos ===
    if (lastStep?.startsWith('photo_analysis_')) {
      const step = Number(lastStep.split('_')[2]);
      if (!Number.isNaN(step)) {
        evaluation.photoStep = step;
      }
    }

    // === Pergunta adicional ===
    if (lastStep?.startsWith('additional_question_')) {
      const step = Number(lastStep.split('_')[2]);
      if (!Number.isNaN(step)) {
        evaluation.additionalQuestionStep = step;
        evaluation.photoStep = step;
      }
    }

    // === Questionário concluído ===
    if (lastStep === PROGRESS_STEPS.QUESTIONNAIRE_COMPLETED) {
      evaluation.questionnaireStep = 999;
    }

    const result = {
      userJourneyStatus: data.userJourneyStatus ?? 'signup_started',
      lastStep,
      evaluation,
      training: { ...defaultTrain, ...data.trainingProgress },

      subscriptionStatus: data.subscriptionStatus ?? 'inactive',

      trainingProgram,

      evaluationId:
        data.subscriptionCycle?.evaluation?.evaluationId ?? null,

      subscriptionCycle:
        data.subscriptionCycle,

      hasSubscriptionHistory:
        data.hasSubscriptionHistory,

      hasSeenWelcomeSubscription:
        data.hasSeenWelcomeSubscription ?? false,

      // Dados comerciais preservados para retomada e auditoria do checkout
      planId:
        data.planId,

      commercialPriceCents:
        data.commercialPriceCents,

      commitmentDurationMonths:
        data.commitmentDurationMonths,

      billingInterval:
        data.billingInterval,

      billingIntervalCount:
        data.billingIntervalCount,

      installmentCount:
        data.installmentCount,

      installmentAmountCents:
        data.installmentAmountCents,

      billingPriceCents:
        data.billingPriceCents,

      currency:
        data.currency,
    };

    return result;
  }

  /**
   * Retorna o evaluationId da avaliação pertencente ao
   * subscriptionCycle atual.
   *
   * Fonte canônica:
   * users/{uid}.subscriptionCycle.evaluation.evaluationId
   *
   * Componentes e serviços da avaliação devem utilizar
   * exclusivamente este método para resolver a identidade
   * da avaliação ativa.
   */
  async getCurrentEvaluationId(): Promise<string> {
    const progress = await this.getCurrentProgress();

    const evaluationId = progress.evaluationId;

    if (
      typeof evaluationId !== 'string' ||
      !evaluationId.trim()
    ) {
      throw new Error(
        '[ProgressTrackerService] evaluationId da avaliação atual não encontrado.'
      );
    }

    return evaluationId;
  }

  /*
   * Retorna lastStep com cache local (TTL: 30s)
   */
  async getLastStep(): Promise<LastStep> {
    const now = Date.now();
    if (this.lastStepCache !== null && (now - this.lastStepTimestamp) < this.CACHE_TTL) {
      return this.lastStepCache;
    }
    const progress = await this.getCurrentProgress();
    this.lastStepCache = progress.lastStep;
    this.lastStepTimestamp = now;
    return this.lastStepCache;
  }

  /* =====================================================
  * MÉTODO PÚBLICO PARA ATUALIZAR FLAGS DE UX
  * -----------------------------------------------------
  * Permite que componentes atualizem campos específicos
  * (ex.: hasSeenWelcomeSubscription) sem expor updateUser inteiro.
  * ===================================================== */
  async updateUserFlag(data: Partial<UserModel>): Promise<void> {
    await this.updateUser(data);
  }
}
